/**
 * Formatação pequena e pura para o módulo Escala — mesma convenção usada no
 * protótipo aprovado (`src/prototipos/escala/componentes/formato.ts`), agora
 * sobre dados reais (`data_hora` ISO vindo do banco, não os campos `data`/
 * `hora` separados do mock). Reaproveita `shared/lib/datas.ts` para tudo que
 * já existe lá — nenhuma lógica de data é duplicada aqui.
 */
import {
  Ban,
  CircleCheck,
  CircleX,
  Hourglass,
  Zap,
  type LucideIcon,
} from "lucide-react";
import {
  DIAS_ABREV,
  MESES,
  chaveDia,
  formatarDataCurta,
  formatarHora,
  rotuloDiaLongo,
} from "@/shared/lib/datas";
import type { EscalaOrigemResposta, EscalaResposta } from "@/shared/types/database.types";

export { formatarDataCurta, formatarHora, rotuloDiaLongo };

/** "Quinta, 16 jul · 19:30". */
export function formatarQuando(iso: string): string {
  return `${rotuloDiaLongo(iso)} · ${formatarHora(iso)}`;
}

export function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  return ((partes[0]?.[0] ?? "") + (partes.length > 1 ? partes[partes.length - 1]![0] : "")).toUpperCase();
}

// ---------------------------------------------------------------------------
// Tons de cor (selos, ícones e fundos tingidos)
// ---------------------------------------------------------------------------

export type Tom = "ok" | "no" | "wait" | "info" | "brand" | "off";

/**
 * Só tokens semânticos existentes (G08). O fundo é uma versão tingida do token
 * do estado e o ÍCONE leva a cor do estado; o TEXTO do selo fica sempre em
 * `text-text`, que tem contraste folgado sobre qualquer um desses fundos nos
 * dois temas — `text-success`/`text-warning` em texto pequeno sobre fundo
 * tingido ficam abaixo de 4,5:1 no tema claro (ESC-ACH-12).
 */
export const FUNDO_TOM: Record<Tom, string> = {
  ok: "bg-success/15",
  no: "bg-danger/10",
  wait: "bg-warning/20",
  info: "bg-info/10",
  brand: "bg-primary/10",
  off: "bg-surface-2",
};

export const COR_ICONE_TOM: Record<Tom, string> = {
  ok: "text-success",
  no: "text-danger",
  wait: "text-warning",
  info: "text-info",
  brand: "text-primary",
  off: "text-muted",
};

// ---------------------------------------------------------------------------
// Resposta do participante
// ---------------------------------------------------------------------------

export interface RotuloResposta {
  texto: string;
  tom: Tom;
  icone: LucideIcon;
}

/**
 * QUA-010 (Matriz v2): selo distinto e legível para os 3 valores de
 * `origem_resposta` (própria/administrativa/automática) + PENDENTE — nunca
 * um JSON cru de `detalhes`. Só reflete `resposta`/`origem_resposta` como o
 * backend gravou.
 */
export function rotuloResposta(resposta: EscalaResposta, origem: EscalaOrigemResposta | null): RotuloResposta {
  const monta = (texto: string, tom: Tom, icone: LucideIcon): RotuloResposta => ({ texto, tom, icone });
  if (resposta === "PENDENTE") return monta("Pendente", "wait", Hourglass);
  if (resposta === "CONFIRMADA") {
    if (origem === "automatica") return monta("Confirmada automaticamente", "ok", Zap);
    if (origem === "administrativa") return monta("Confirmada pela liderança", "ok", CircleCheck);
    return monta("Confirmada", "ok", CircleCheck);
  }
  // RECUSADA
  if (origem === "administrativa") return monta("Recusada pela liderança", "no", CircleX);
  return monta("Recusada", "no", CircleX);
}

/**
 * Marcador ÚNICO do card na aba "Agendadas": só a resposta da própria pessoa,
 * em primeira pessoa. (Escala cancelada e escala realizada não mostram a
 * resposta no card — fica para ver dentro da escala.)
 */
export function marcadorMinhaResposta(resposta: EscalaResposta, origem: EscalaOrigemResposta | null): RotuloResposta {
  const base = rotuloResposta(resposta, origem);
  if (resposta === "PENDENTE") return { ...base, texto: "Aguardando sua resposta" };
  if (resposta === "CONFIRMADA") {
    if (origem === "automatica") return { ...base, texto: "Você está na equipe" };
    if (origem === "administrativa") return { ...base, texto: "Confirmada pela liderança" };
    return { ...base, texto: "Você confirmou" };
  }
  if (origem === "administrativa") return { ...base, texto: "Recusada pela liderança" };
  return { ...base, texto: "Você recusou" };
}

export const ICONE_CANCELADA = Ban;

// ---------------------------------------------------------------------------
// Classificação da escala nas abas da lista
// ---------------------------------------------------------------------------

export type AbaEscala = "agendadas" | "realizadas" | "canceladas";

/** Escala ativa e ainda por acontecer. */
export function ehFutura(dataHoraISO: string, agora: Date = new Date()): boolean {
  return new Date(dataHoraISO) > agora;
}

/**
 * Agendadas = ativa e no futuro. Realizadas = ativa e já passou.
 * Canceladas = sempre aqui, mesmo com data futura (nunca entra em "Realizadas").
 */
export function abaDaEscala(
  escala: { situacao: string; data_hora: string },
  agora: Date = new Date(),
): AbaEscala {
  if (escala.situacao === "cancelada") return "canceladas";
  return ehFutura(escala.data_hora, agora) ? "agendadas" : "realizadas";
}

// ---------------------------------------------------------------------------
// Datas relativas e blocos de data
// ---------------------------------------------------------------------------

function inicioDoDia(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Diferença em dias de CALENDÁRIO (meia-noite a meia-noite) entre `a` e `b` (b − a). */
export function diasDeCalendario(a: Date, b: Date): number {
  return Math.round((inicioDoDia(b).getTime() - inicioDoDia(a).getTime()) / 86_400_000);
}

/** "hoje", "amanhã" ou "em N dias" para uma data futura (ou de hoje). */
export function rotuloFuturo(iso: string, agora: Date = new Date()): string {
  const n = diasDeCalendario(agora, new Date(iso));
  if (n <= 0) return "hoje";
  if (n === 1) return "amanhã";
  return `em ${n} dias`;
}

/** "agora", "há 12 min", "há 3 h", "ontem" ou "há N dias" para um instante passado. */
export function tempoRelativo(iso: string, agora: Date = new Date()): string {
  const d = new Date(iso);
  const min = Math.round((agora.getTime() - d.getTime()) / 60_000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const dias = diasDeCalendario(d, agora);
  if (dias === 0) return `há ${Math.round(min / 60)} h`;
  if (dias === 1) return "ontem";
  return `há ${dias} dias`;
}

/** "Hoje", "Ontem" ou "Quinta, 16 jul" — cabeçalho de grupo por dia do histórico. */
export function rotuloDiaRelativo(iso: string, agora: Date = new Date()): string {
  const dias = diasDeCalendario(new Date(iso), agora);
  if (dias === 0) return "Hoje";
  if (dias === 1) return "Ontem";
  return rotuloDiaLongo(iso);
}

/** "qui, 16 jul" — data curta com o dia da semana. */
export function diaCurtoComSemana(iso: string): string {
  const d = new Date(iso);
  return `${DIAS_ABREV[d.getDay()]!.toLowerCase()}, ${d.getDate()} ${MESES[d.getMonth()]}`;
}

/** Partes para o bloco de data ("OUT" / "04" / "DOM"). */
export function partesDoBloco(iso: string): { mes: string; dia: string; semana: string } {
  const d = new Date(iso);
  return {
    mes: MESES[d.getMonth()]!,
    dia: String(d.getDate()).padStart(2, "0"),
    semana: DIAS_ABREV[d.getDay()]!,
  };
}

export { chaveDia };

// ---------------------------------------------------------------------------
// Histórico
// ---------------------------------------------------------------------------

/** Rótulo legível por tipo de evento do histórico (append-only, 10 tipos possíveis). */
export const ROTULO_HISTORICO: Record<string, string> = {
  criacao: "Escala criada",
  edicao_nome_descricao: "Nome ou descrição editados",
  edicao_data_hora: "Data e hora alteradas",
  cancelamento: "Escala cancelada",
  inclusao: "Participante escalado",
  remocao: "Participante removido",
  alteracao_funcao: "Função alterada",
  confirmacao: "Confirmação registrada",
  recusa: "Recusa registrada",
  resposta_administrativa: "Resposta registrada pela liderança",
};
